import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Quote, Shield, Swords, Anchor, Crown, Plus } from "lucide-react";
import { FadeUp, MaskLine, SectionTag, Marquee } from "../components/site/Reveal";
import {
  IMAGES,
  ROY_PHOTO,
  STATS,
  MARQUEE_ITEMS,
  MANIFESTO,
  NETWORK_STATS,
  ECOSYSTEM_FORMULA,
  SERVICES,
  TESTIMONIALS,
} from "../content";

const CHAPTER_ICONS = [Crown, Shield, Swords, Anchor];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 120]);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute -top-24 left-[-12%] h-[30rem] w-[30rem] rounded-full bg-gold/20 blur-[130px]" />
      <div className="absolute right-[-8%] top-1/3 h-[24rem] w-[24rem] rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 pt-36 lg:grid-cols-12 lg:px-10 lg:pt-40">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.35em] text-golddeep"
            data-testid="hero-eyebrow"
          >
            Roy Buncome · Strategy Architect · CollabIgnite
          </motion.p>

          <h1 className="font-serif text-5xl font-bold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.35}>You built something real.</MaskLine>
            <MaskLine delay={0.5}>Now raise the money</MaskLine>
            <MaskLine delay={0.65}>and <span className="gold-text italic">exit</span> like it.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subhead"
          >
            You get a strategy architect in your corner: 25 years of reading rooms in
            seconds, spotting who can actually write the check, and putting you at their
            table. Three companies exited, $30M to $100M. Raising for the first time?
            Yours should land between $4M and $8M, architected right.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/book" data-testid="hero-book-call-btn" className="btn-gold">
              Book a 20-Minute Call <ArrowRight size={16} />
            </Link>
            <Link to="/working-with-me" data-testid="hero-working-btn" className="btn-ghost">
              See How It Works
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, delay: 0.7 }}
          className="relative hidden lg:col-span-5 lg:block"
        >
          <motion.div style={{ y }} className="relative">
            <div className="absolute -inset-3 border border-gold/60" />
            <img
              src={ROY_PHOTO}
              alt="Roy Buncome, Strategy Architect"
              className="h-[520px] w-full object-cover object-top"
              data-testid="hero-portrait-img"
            />
            <div className="absolute bottom-6 left-6 right-6 border border-gold/50 bg-paper/95 p-5 shadow-[0_16px_40px_-16px_rgba(19,26,38,0.35)] backdrop-blur">
              <p className="font-serif text-xl font-semibold text-ink">Roy Buncome</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-golddeep">
                Strategy Architect · Your wingman in the room
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="relative mx-auto max-w-7xl px-6 lg:px-10"
        data-testid="hero-stats"
      >
        <div className="grid grid-cols-2 gap-8 border-t border-ink/15 py-10 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-serif text-2xl font-semibold text-golddeep sm:text-4xl">{s.value}</p>
              <p className="mt-1.5 text-xs uppercase tracking-wider text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const ManifestoSection = () => (
  <section data-testid="manifesto-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>The Manifesto</SectionTag>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Tough in the room. <span className="gold-text italic">Steady</span> on your side.
      </h2>
    </FadeUp>
    <div className="mt-16 grid gap-px bg-gold/25 sm:grid-cols-2">
      {MANIFESTO.map((m, i) => {
        const Icon = CHAPTER_ICONS[i];
        return (
          <FadeUp key={m.num} delay={i * 0.08}>
            <article
              data-testid={`manifesto-chapter-${m.num}`}
              className="group relative h-full bg-paper p-10 transition-colors duration-500 hover:bg-cream"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-sm text-golddeep/70">{m.num}</span>
                <Icon
                  size={22}
                  className="text-golddeep/50 transition-colors duration-500 group-hover:text-golddeep"
                />
              </div>
              <h3 className="mt-8 font-serif text-2xl font-semibold text-ink sm:text-3xl">
                {m.title}
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{m.body}</p>
            </article>
          </FadeUp>
        );
      })}
    </div>
  </section>
);

const NetworkSection = () => (
  <section data-testid="network-section" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <div className="grid items-start gap-14 lg:grid-cols-2">
        <FadeUp>
          <SectionTag>The Power of the Network</SectionTag>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Your net worth <span className="gold-text italic">is</span> your network.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
            You get plugged directly into an elite ecosystem of operators, capital
            allocators, and strategic partners. Stop knocking on doors. You get the keys.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-8">
            {NETWORK_STATS.map((s) => (
              <div key={s.label} data-testid={`network-stat-${s.label.split(" ")[0].toLowerCase()}`}>
                <p className="font-serif text-3xl font-semibold text-ink sm:text-4xl">{s.value}</p>
                <p className="mt-1.5 text-xs uppercase tracking-wider text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="border border-gold/40 bg-white p-8 sm:p-10">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-golddeep">
              The CollabIgnite formula
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {ECOSYSTEM_FORMULA.map((item, i) => (
                <span key={item} className="flex items-center gap-3">
                  <span className="border border-gold/40 bg-paper px-4 py-2 font-serif text-lg font-medium text-ink">
                    {item}
                  </span>
                  {i < ECOSYSTEM_FORMULA.length - 1 && (
                    <Plus size={16} className="text-golddeep" />
                  )}
                </span>
              ))}
            </div>
            <div className="mt-8 border-t border-gold/25 pt-6">
              <p className="font-serif text-2xl font-semibold italic text-ink">
                Collaboration Ignited. <span className="gold-text">Growth Accelerated.</span>
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Great businesses are not built alone. The right relationships, resources,
                knowledge, strategy, and capital create the spark that transforms your
                idea into a scalable enterprise.
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const ExitsSection = () => (
  <section data-testid="exits-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <div className="grid items-center gap-14 lg:grid-cols-2">
      <FadeUp>
        <SectionTag>Receipts</SectionTag>
        <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Three exits. <span className="gold-text italic">$30M to $100M.</span>
        </h2>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
          Talk is cheap. You want receipts. These deals were structured, negotiated, and
          closed under real pressure, with the right people at the table and your kind of
          money on the line. The exit you want gets architected years before it gets announced.
        </p>
        <div className="mt-10 flex flex-wrap items-end gap-10">
          <div>
            <p className="font-serif text-6xl font-bold text-ink sm:text-7xl">3</p>
            <p className="mt-2 text-xs uppercase tracking-wider text-slate-500">Companies exited</p>
          </div>
          <div className="border-l border-gold/40 pl-10">
            <p className="font-serif text-3xl font-semibold text-ink sm:text-5xl">$30M to $100M</p>
            <p className="mt-2 text-xs uppercase tracking-wider text-slate-500">Range of exits architected</p>
          </div>
        </div>
        <div className="mt-10 border-l-2 border-gold bg-cream p-6">
          <p className="font-serif text-xl italic text-ink" data-testid="first-raise-callout">
            Raising for the first time? Yours should expect $4M to $8M, sized and sequenced
            so your second raise is easier than your first.
          </p>
          <p className="mt-4 text-xs uppercase tracking-wider text-slate-500">
            Client names stay confidential. The numbers do the talking.
          </p>
        </div>
      </FadeUp>
      <FadeUp delay={0.15}>
        <div className="relative">
          <div className="absolute -inset-3 border border-gold/60" />
          <img
            src={IMAGES.boardroom}
            alt="Executive boardroom where deals close"
            className="h-[460px] w-full object-cover"
          />
          <p className="absolute bottom-0 left-0 right-0 bg-paper/90 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-golddeep backdrop-blur">
            Where your deal closes
          </p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const ServicesSection = () => (
  <section data-testid="services-section" className="border-t border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>What You Get</SectionTag>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Six ways your outcome moves
        </h2>
      </FadeUp>
      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <FadeUp key={s.title} delay={i * 0.06}>
            <article
              data-testid={`service-card-${i}`}
              className="group h-full border border-gold/25 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-golddeep/60 hover:shadow-[0_20px_50px_-20px_rgba(140,109,31,0.35)]"
            >
              <span className="font-mono text-xs text-golddeep/60">0{i + 1}</span>
              <h3 className="mt-4 font-serif text-xl font-semibold text-ink">{s.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{s.body}</p>
            </article>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section data-testid="testimonials-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>Word of Mouth</SectionTag>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        What the network says
      </h2>
    </FadeUp>
    <div className="mt-16 grid gap-6 md:grid-cols-2">
      {TESTIMONIALS.map((t, i) => (
        <FadeUp key={t.name} delay={i * 0.07}>
          <figure
            data-testid={`testimonial-card-${i}`}
            className="h-full border border-gold/25 bg-white p-8"
          >
            <Quote size={22} className="text-gold" />
            <blockquote className="mt-5 font-serif text-lg italic leading-relaxed text-slate-700">
              "{t.quote}"
            </blockquote>
            <figcaption className="mt-6 border-t border-gold/20 pt-4">
              <p className="text-sm font-semibold text-ink">{t.name}</p>
              <p className="text-xs text-slate-500">{t.role}</p>
            </figcaption>
          </figure>
        </FadeUp>
      ))}
    </div>
    <FadeUp delay={0.2}>
      <p className="mt-8 text-xs text-slate-500">
        Excerpted from public recommendations on{" "}
        <a
          data-testid="testimonials-linkedin-link"
          href="https://www.linkedin.com/in/roybuncome"
          target="_blank"
          rel="noopener noreferrer"
          className="text-golddeep underline-offset-4 hover:underline"
        >
          LinkedIn
        </a>
        .
      </p>
    </FadeUp>
  </section>
);

const CtaBanner = () => (
  <section data-testid="cta-banner" className="relative overflow-hidden border-t border-gold/25 bg-paper">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,162,39,0.22),transparent_65%)]" />
    <div className="relative mx-auto max-w-5xl px-6 py-32 text-center lg:px-10">
      <FadeUp>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-golddeep">Ready to Ignite?</p>
        <h2 className="mt-6 font-serif text-4xl font-bold tracking-tight text-ink sm:text-5xl lg:text-6xl">
          The room is waiting.<br />
          <span className="gold-text italic">Twenty minutes is all it takes.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base text-slate-600">
          The difference between linear growth and exponential scale is the decision you
          make right now. Bring the raise, the deal, the competitor, or the exit you are
          chasing. You leave the call knowing your first move.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/book" data-testid="cta-book-btn" className="btn-gold">
            Book Your Call <ArrowRight size={16} />
          </Link>
          <Link to="/about" data-testid="cta-about-btn" className="btn-ghost">
            Research Roy First
          </Link>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <ManifestoSection />
      <NetworkSection />
      <ExitsSection />
      <ServicesSection />
      <TestimonialsSection />
      <CtaBanner />
    </main>
  );
}
